/**
 * Records & Data Grid Types
 * Shared types for data grid functionality and record queries
 *
 * IMPORTANT - API CASING CONVENTION:
 * - All API endpoints use snake_case for property names (e.g., column_id, data_type)
 * - This matches database column naming and Hono/Node.js backend conventions
 * - The @loader/data-grid package has its own camelCase versions for frontend use
 * - Frontend components must transform snake_case → camelCase when consuming API data
 *
 * NOTE: Records are now stored as Parquet files in R2, not in PostgreSQL.
 * The API returns records queried from Parquet via DuckDB API.
 *
 * Note: Response types are inferred via Hono RPC
 * Route constants are now defined in the backend
 */

import type { DataGridFilterOperator } from '../constants/common';

// Re-export for backwards compatibility
export type { DataGridFilterOperator } from '../constants/common';

// ============================================================================
// Diff Types
// ============================================================================

/**
 * Change type filter for diff queries
 *
 * Controls which records are returned when comparing two versions:
 * - 'all': Returns both added and removed records
 * - 'added': Returns only records that exist in the right version but not the left
 * - 'removed': Returns only records that exist in the left version but not the right
 */
export type DiffChangeType = 'all' | 'added' | 'removed';

// ============================================================================
// Data Grid Types
// ============================================================================

/**
 * Sort direction for data grid columns
 */
export type DataGridSortDirection = 'asc' | 'desc';

/**
 * Filter value types for data grid filtering
 */
export type DataGridFilterValue =
  | string
  | number
  | boolean
  | string[]
  | null;

/**
 * Filter definition for data grid
 *
 * NOTE: Uses snake_case to match API/database convention.
 * The data-grid package has its own camelCase version for frontend use.
 * TODO: Unify these types in a future refactor.
 */
export interface DataGridFilter {
  column_id: string;
  operator?: DataGridFilterOperator;
  value: DataGridFilterValue;
}

/**
 * Request parameters for data grid queries
 *
 * NOTE: Uses snake_case to match API/database convention.
 * The data-grid package has its own camelCase version for frontend use.
 */
export interface DataGridRequest {
  limit: number;
  offset: number;
  sort_by?: string;
  sort_dir?: DataGridSortDirection;
  filters?: DataGridFilter[];
  search?: string;
}

/**
 * Data Grid Column Schema
 *
 * Optional schema information for columns in the response.
 *
 * NOTE: Uses snake_case to match API/database convention.
 * The data-grid package has its own camelCase version for frontend use.
 * TODO: Unify these types in a future refactor.
 */
export interface DataGridColumnSchema {
  column_id: string;
  label: string;
  data_type: string;
  sortable?: boolean;
  filterable?: boolean;
  description?: string;
}

/**
 * Repository result format (used internally by API)
 *
 * This is the raw result from repository queries before
 * being wrapped in a DataGridResponse.
 */
export interface DataGridRepositoryResult<TData> {
  rows: TData[];
  total: number;
  schema?: DataGridColumnSchema[];
}

/**
 * Sentinel value indicating the total count should be retrieved from cache.
 *
 * Used in pagination optimization: count queries only run on the first page (offset=0).
 * Subsequent pages return this value, and the frontend uses a cached total.
 *
 * Flow:
 * 1. First page (offset=0): Backend runs count query → returns real total
 * 2. Frontend caches the total
 * 3. Subsequent pages: Backend skips count query → returns PAGINATION_TOTAL_USE_CACHE
 * 4. Frontend detects -1 and uses cached total
 */
export const PAGINATION_TOTAL_USE_CACHE = -1 as const;

/**
 * Type guard to check if pagination total indicates "use cached value".
 *
 * @param total - The total value from pagination response
 * @returns true if the value equals PAGINATION_TOTAL_USE_CACHE (-1)
 *
 * @example
 * ```typescript
 * const { total } = response.pagination;
 * if (!isPaginationTotalCached(total)) {
 *   // Real count - cache it
 *   cachedTotal.current = total;
 * }
 * // Use cached value when total is -1
 * const displayTotal = isPaginationTotalCached(total) ? cachedTotal.current : total;
 * ```
 */
export function isPaginationTotalCached(total: number): boolean {
  return total === PAGINATION_TOTAL_USE_CACHE;
}

/**
 * Data Grid Pagination Info
 *
 * Pagination metadata for data grid responses.
 *
 * @property total - Total row count, or -1 (PAGINATION_TOTAL_USE_CACHE) to signal
 *                   "use cached value from first page". This optimization prevents
 *                   running expensive count queries on every page change.
 */
export interface DataGridPagination {
  /**
   * Total number of rows matching the query.
   *
   * Returns -1 (PAGINATION_TOTAL_USE_CACHE) on non-first pages to signal
   * that the frontend should use a cached value from the first page request.
   * This prevents running expensive count queries on every page change.
   */
  total: number;
  /** Maximum rows per page */
  limit: number;
  /** Number of rows skipped */
  offset: number;
  /** Remaining rows after current page: max(0, total - offset - limit) */
  remaining: number;
}

/**
 * Data Grid Response
 *
 * Generic response format for paginated data grids.
 * Used by records endpoint and linked table data endpoint.
 */
export interface DataGridResponse<TData = Record<string, unknown>> {
  data: TData[];
  pagination: DataGridPagination;
  schema?: DataGridColumnSchema[];
}

/**
 * API Response wrapper for record list
 *
 * Records are queried from Parquet files via DuckDB API.
 * Each record is a dynamic object with field names as keys.
 */
export interface ListRecordsResponse extends DataGridResponse<Record<string, unknown>> {
  // Inherits data, pagination, and schema from DataGridResponse
}
